import type { TFunction } from "i18next";

import { FAILED_OUTCOME, parseEventType } from "./event-types";
import { NOTIFICATION_SETTING_GROUPS } from "./setting-groups";
import type {
  GetNotificationSettingsResponse,
  GroupedNotificationSettings,
  NotificationSetting,
  NotificationSettingEntry,
  NotificationSettingText,
} from "./types";

type NotificationSettingType = NotificationSetting["type"];

export function setSettingsEnabled(
  prev: GetNotificationSettingsResponse | undefined,
  { types, enabled }: Pick<NotificationSettingEntry, "types" | "enabled">,
): GetNotificationSettingsResponse | undefined {
  if (!prev) return prev;
  return {
    ...prev,
    settings: prev.settings.map((setting) =>
      types.includes(setting.type) ? { ...setting, enabled } : setting,
    ),
  };
}

function getEntryId(type: NotificationSettingType): NotificationSettingEntry["id"] {
  const { domain, outcome } = parseEventType(type);
  return outcome === FAILED_OUTCOME ? `${domain}:${FAILED_OUTCOME}` : type;
}

function toSettingEntries(settings: NotificationSetting[]): NotificationSettingEntry[] {
  const entries = new Map<NotificationSettingEntry["id"], NotificationSettingEntry>();
  for (const { type, enabled } of settings) {
    const id = getEntryId(type);
    const entry = entries.get(id);
    if (entry) entry.types.push(type);
    else entries.set(id, { id, types: [type], enabled });
  }
  return [...entries.values()];
}

export function groupSettings(settings: NotificationSetting[]): GroupedNotificationSettings[] {
  return NOTIFICATION_SETTING_GROUPS.map((group) => ({
    ...group,
    entries: toSettingEntries(
      settings.filter((setting) => group.prefixes.some((prefix) => setting.type.startsWith(prefix))),
    ),
  })).filter((group) => group.entries.length > 0);
}

export function getSettingText(t: TFunction, id: NotificationSettingEntry["id"]): NotificationSettingText {
  const translationKey = `notificationSettings.types.${id}`;
  return {
    label: t(`${translationKey}.label`, { defaultValue: id }),
    description: t(`${translationKey}.description`, {
      defaultValue: t("notificationSettings.unknownTypeDescription"),
    }),
  };
}

export function filterSettingGroups(
  groups: GroupedNotificationSettings[],
  query: string,
  getText: (id: NotificationSettingEntry["id"]) => NotificationSettingText,
): GroupedNotificationSettings[] {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  if (!normalizedQuery) return groups;
  const matches = (id: NotificationSettingEntry["id"]) => {
    const { label, description } = getText(id);
    return (
      label.toLocaleLowerCase().includes(normalizedQuery) ||
      description.toLocaleLowerCase().includes(normalizedQuery)
    );
  };
  return groups
    .map((group) => ({
      ...group,
      entries: group.entries.filter(
        (entry) => matches(entry.id) || entry.types.some(matches),
      ),
    }))
    .filter((group) => group.entries.length > 0);
}
